/* ==========================================================================
   DANG — Product Page
   Responsibilities:
     1. Horizontal scroll gallery + Sunski-style dash indicators
     2. Per-COLOR image swap — when a color is selected, show that color's
        photos and scroll to the first. Keyed by color (not full variant) so
        it works for two-axis goggles (Color × Strap) even when the chosen
        combo is unavailable and Shopify returns a null variant.
     3. Click-to-advance gallery (replaces Dawn's lightbox) + swipe (native)
     4. Variant-aware badge row toggle
   Color → media data comes from [data-color-media-map] in main-product.liquid.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  /* Both Dawn's mobile slider and our desktop layout use overflow-x scroll
     + scroll-snap, so the same scrollTo()/click-advance logic works on both.
     `isDesktop` only gates the dot-indicator UI, which is a desktop visual. */
  var isDesktop = !window.matchMedia('(max-width: 749px)').matches;
  /* Goggle PDPs use a vertical stacked gallery (added via the
     .dang-product--goggles class on <product-info> when product.type
     == "Goggles"). When this is true we skip the horizontal-gallery UI. */
  var isGoggle = document.querySelector('.dang-product--goggles') !== null;

  /* ---------------------------------------------------------------
     Parse the color → media map. Shape: { "<color>": [<media_id>, ...] }.
     allColorMediaIds = union of every color's media (used to decide which
     images "belong" to some color vs. are shared/product-level).
     --------------------------------------------------------------- */
  var colorMediaMap = {};
  var allColorMediaIds = new Set();
  var mapScript = document.querySelector('[data-color-media-map]');
  if (mapScript) {
    try {
      colorMediaMap = JSON.parse(mapScript.textContent);
      Object.keys(colorMediaMap).forEach(function (color) {
        (colorMediaMap[color] || []).forEach(function (mediaId) {
          allColorMediaIds.add(String(mediaId));
        });
      });
    } catch (e) {
      /* Bad JSON shouldn't break the page — color sync just no-ops */
    }
  }
  /* "Strict" mode = at least one color has assigned media on this product. Then
     untagged images stay hidden so each color shows only its own photos. If NO
     color has media, we fall back to showing everything (untagged products). */
  var strictMode = allColorMediaIds.size > 0;

  /* Valid Color × Strap pairs, for auto-selecting a compatible strap when a
     color is chosen (keeps the variant available so the price doesn't vanish). */
  var variantOptions = [];
  var voScript = document.querySelector('[data-variant-options]');
  if (voScript) {
    try {
      variantOptions = JSON.parse(voScript.textContent);
    } catch (e) {}
  }
  var hasStrapOption = variantOptions.some(function (o) {
    return o.s != null;
  });

  /* When a color is selected on a two-option product (Color × Strap), the
     previously-selected strap may not pair with the new color, leaving the
     variant unavailable (no price, can't add to cart). Auto-select the first
     strap that DOES pair with this color so the variant stays valid. Clicking
     the strap radio re-triggers Dawn's variant resolution. No-op for
     single-option products (sunglasses). */
  function ensureValidStrapForColor(colorValue) {
    if (!hasStrapOption) return;
    var radios = Array.prototype.slice.call(
      document.querySelectorAll('.product-form__input input[type="radio"]')
    );
    var isColor = function (val) {
      return Object.prototype.hasOwnProperty.call(colorMediaMap, val);
    };
    var currentStrap = null;
    radios.forEach(function (r) {
      if (r.checked && !isColor(r.value)) currentStrap = r.value;
    });
    var alreadyValid = variantOptions.some(function (o) {
      return o.c === colorValue && o.s === currentStrap;
    });
    if (alreadyValid) return;

    var firstValid = null;
    for (var i = 0; i < variantOptions.length; i++) {
      if (variantOptions[i].c === colorValue) {
        firstValid = variantOptions[i];
        break;
      }
    }
    if (!firstValid || firstValid.s == null) return;

    for (var j = 0; j < radios.length; j++) {
      if (radios[j].value === firstValid.s && !isColor(radios[j].value)) {
        if (!radios[j].checked) radios[j].click();
        break;
      }
    }
  }

  function mediaIdsForColor(colorValue) {
    return (colorMediaMap[colorValue] || []).map(String);
  }

  /* The gallery <li>s render with data-media-id="{section.id}-{media.id}".
     Section IDs contain dashes, so split on the LAST dash for the media id. */
  function mediaIdFromLi(li) {
    var raw = li.getAttribute('data-media-id') || '';
    var dash = raw.lastIndexOf('-');
    return dash === -1 ? raw : raw.slice(dash + 1);
  }

  /* The currently-selected COLOR is the source of truth for which image shows.
     We read the checked color radio (its value is a color = a key in the map)
     because that reflects the user's choice even when the full Color × Strap
     combo is unavailable (selected variant null). Falls back to the variant's
     option1 if no color radio is found. */
  function currentColor() {
    var checked = document.querySelectorAll(
      '.product-form__input input[type="radio"]:checked'
    );
    for (var i = 0; i < checked.length; i++) {
      if (Object.prototype.hasOwnProperty.call(colorMediaMap, checked[i].value)) {
        return checked[i].value;
      }
    }
    var script = document.querySelector('[data-selected-variant]');
    if (script) {
      try {
        var v = JSON.parse(script.textContent);
        if (v && v.option1) return v.option1;
      } catch (e) {}
    }
    return null;
  }

  /* ---------------------------------------------------------------
     Per-gallery setup. Exposes track.__dangApplyColor(colorValue).
     --------------------------------------------------------------- */
  var galleries = document.querySelectorAll('.product__media-list');

  galleries.forEach(function (track) {
    var allItems = Array.prototype.slice.call(
      track.querySelectorAll(':scope > .product__media-item')
    );
    if (allItems.length <= 1) return; /* nothing to swap/indicate for one image */

    var visibleItems = allItems.filter(isVisible);

    function isVisible(el) {
      var s = window.getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden';
    }

    /* Goggles use a vertical stacked layout — every image for the selected
       color is already visible at once, the user scrolls the page to see
       more. Skip click-to-advance, dot indicators, and counter sync. Keep
       a no-op click handler that stops Dawn's lightbox from firing, and
       expose color-based filtering via __dangApplyColor so swatch clicks
       still swap to the active color's images. applyColorCore is a function
       declaration, hoisted to the top of this forEach scope, so it's
       callable here even though it's defined below. */
    /* DESKTOP ONLY. On mobile (<750px) the vertical stack means lots of page
       scrolling, so goggles fall through to the same horizontal swipe gallery
       as shades (swipe + "1 / N" counter). Per Matt: scroll-to-see-more is poor
       on mobile — match the shades gallery there. */
    if (isGoggle && isDesktop) {
      track.addEventListener(
        'click',
        function (e) {
          var clickedLi = e.target.closest('.product__media-item');
          if (!clickedLi || !track.contains(clickedLi)) return;
          e.preventDefault();
          e.stopImmediatePropagation();
        },
        true
      );
      track.__dangApplyColor = function (colorValue) {
        applyColorCore(colorValue);
      };
      return;
    }

    /* Click-to-advance: click any visible image → scroll to the next visible
       one (wraps at the end). Capture phase + stopImmediatePropagation keeps
       Dawn's <modal-opener> lightbox from firing. Works desktop + mobile. */
    track.addEventListener(
      'click',
      function (e) {
        var clickedLi = e.target.closest('.product__media-item');
        if (!clickedLi || !track.contains(clickedLi)) return;
        e.preventDefault();
        e.stopImmediatePropagation();

        var live = Array.prototype.slice
          .call(track.querySelectorAll(':scope > .product__media-item'))
          .filter(isVisible);
        if (live.length <= 1) return;

        var idx = live.indexOf(clickedLi);
        if (idx === -1) return;
        var next = live[(idx + 1) % live.length];
        if (next) track.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
      },
      true
    );

    /* Core show/hide for a color. Re-queries LIs (Dawn swaps them on variant
       change). Returns the color's ordered media-id list (for scrolling). */
    function applyColorCore(colorValue) {
      allItems = Array.prototype.slice.call(
        track.querySelectorAll(':scope > .product__media-item')
      );
      var ids = mediaIdsForColor(colorValue);
      var set = new Set(ids);
      var colorHasImages = ids.length > 0;

      allItems.forEach(function (li) {
        var mid = mediaIdFromLi(li);
        if (set.has(mid)) {
          /* belongs to the active color — show */
          li.hidden = false;
          li.style.display = '';
        } else if (allColorMediaIds.has(mid)) {
          /* belongs to a DIFFERENT color — always hide */
          li.hidden = true;
          li.style.display = 'none';
        } else if (colorHasImages && strictMode) {
          /* untagged "extra", this color is specified — hide */
          li.hidden = true;
          li.style.display = 'none';
        } else {
          /* untagged AND (this color has no images of its own, or product
             doesn't use color media at all) — show as graceful fallback */
          li.hidden = false;
          li.style.display = '';
        }
      });

      visibleItems = allItems.filter(isVisible);
      return ids;
    }

    function scrollToFirst(ids) {
      requestAnimationFrame(function () {
        var firstId = ids[0];
        if (!firstId) return;
        var firstLi = null;
        for (var i = 0; i < allItems.length; i++) {
          if (mediaIdFromLi(allItems[i]) === firstId) {
            firstLi = allItems[i];
            break;
          }
        }
        if (firstLi) track.scrollTo({ left: firstLi.offsetLeft, behavior: 'smooth' });
      });
    }

    /* ---- Mobile: no dot row; just sync Dawn's "1 / N" counter ---- */
    if (!isDesktop) {
      var wrapperMobile = track.closest('.product__media-wrapper');
      var counterTotalMobile =
        wrapperMobile && wrapperMobile.querySelector('.slider-counter--total');
      var counterCurrentMobile =
        wrapperMobile && wrapperMobile.querySelector('.slider-counter--current');

      track.__dangApplyColor = function (colorValue) {
        var ids = applyColorCore(colorValue);
        if (counterTotalMobile) counterTotalMobile.textContent = visibleItems.length;
        if (counterCurrentMobile) counterCurrentMobile.textContent = '1';
        scrollToFirst(ids);
      };
      return;
    }

    /* ---- Desktop: dot indicator row ---- */
    var dots = document.createElement('div');
    dots.className = 'dang-gallery-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Product image indicators');
    track.parentNode.insertBefore(dots, track.nextSibling);

    var wrapper = track.closest('.product__media-wrapper');
    var counterTotal = wrapper && wrapper.querySelector('.slider-counter--total');
    var counterCurrent = wrapper && wrapper.querySelector('.slider-counter--current');

    function rebuildDots() {
      dots.innerHTML = '';
      visibleItems.forEach(function (_, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className =
          'dang-gallery-dots__dot' +
          (index === 0 ? ' dang-gallery-dots__dot--active' : '');
        dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
        dot.setAttribute('data-index', index);
        dot.addEventListener('click', function () {
          var target = visibleItems[index];
          if (target) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(dot);
      });

      if (counterTotal && counterTotal.textContent != visibleItems.length) {
        counterTotal.textContent = visibleItems.length;
      }
      if (counterCurrent) counterCurrent.textContent = '1';
    }

    rebuildDots();

    /* Keep the slider-counter total synced even if Dawn's JS rewrites it back */
    if (counterTotal) {
      var totalObserver = new MutationObserver(function () {
        if (counterTotal.textContent != visibleItems.length) {
          counterTotal.textContent = visibleItems.length;
        }
      });
      totalObserver.observe(counterTotal, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    /* Update active dot (and Dawn's counter) based on scroll position */
    function updateActiveDot() {
      if (!visibleItems.length) return;
      var scrollLeft = track.scrollLeft;
      var itemWidth =
        visibleItems[0].offsetWidth + parseFloat(getComputedStyle(track).gap || 0);
      var currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex < 0) currentIndex = 0;
      if (currentIndex >= visibleItems.length) currentIndex = visibleItems.length - 1;

      dots.querySelectorAll('.dang-gallery-dots__dot').forEach(function (d, i) {
        d.classList.toggle('dang-gallery-dots__dot--active', i === currentIndex);
      });
      if (counterCurrent) counterCurrent.textContent = currentIndex + 1;
    }

    var scrollTimer;
    track.addEventListener('scroll', function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateActiveDot, 50);
    });

    track.__dangApplyColor = function (colorValue) {
      var ids = applyColorCore(colorValue);
      rebuildDots();
      scrollToFirst(ids);
    };
  });

  /* ---------------------------------------------------------------
     Lightbox / image-zoom modal sync — same color rule as the gallery.
     (The lightbox is suppressed by click-to-advance, but we keep this in
     sync in case it's ever re-enabled.)
     --------------------------------------------------------------- */
  function syncModalToColor(colorValue) {
    var modal = document.querySelector('product-modal');
    if (!modal) return;
    var ids = mediaIdsForColor(colorValue);
    var set = new Set(ids);
    var colorHasImages = ids.length > 0;
    modal.querySelectorAll('[data-media-id]').forEach(function (el) {
      var mid = String(el.getAttribute('data-media-id') || '');
      if (set.has(mid)) {
        el.hidden = false;
        el.style.display = '';
      } else if (allColorMediaIds.has(mid)) {
        el.hidden = true;
        el.style.display = 'none';
      } else if (colorHasImages && strictMode) {
        el.hidden = true;
        el.style.display = 'none';
      } else {
        el.hidden = false;
        el.style.display = '';
      }
    });
  }

  /* ---------------------------------------------------------------
     Variant-aware badges — keyed by the selected variant id (per-variant
     data like weight). Only runs when a real variant is selected.
     --------------------------------------------------------------- */
  function syncBadgesToVariant(variant) {
    var groups = document.querySelectorAll('.dang-product__badges');
    if (!groups.length) return;
    groups.forEach(function (el) {
      el.hidden = String(el.dataset.variantId) !== String(variant.id);
    });
  }

  /* Variant-aware Frame/Lens/Strap spec line — same toggle as the badges. */
  function syncSpecToVariant(variant) {
    var specs = document.querySelectorAll('.dang-product__spec');
    if (!specs.length) return;
    specs.forEach(function (el) {
      el.hidden = String(el.dataset.variantId) !== String(variant.id);
    });
  }

  /* Variant-aware VLT indicator line (dang-product-vlt.liquid) — same toggle:
     every variant's line is in the DOM, only the selected one is shown. */
  function syncVltToVariant(variant) {
    var lines = document.querySelectorAll('.dang-product__vlt');
    if (!lines.length) return;
    lines.forEach(function (el) {
      el.hidden = String(el.dataset.variantId) !== String(variant.id);
    });
  }

  /* Apply a color across every gallery + the modal. */
  function applyColorEverywhere(colorValue) {
    if (colorValue == null) return;
    document.querySelectorAll('.product__media-list').forEach(function (track) {
      if (typeof track.__dangApplyColor === 'function') {
        track.__dangApplyColor(colorValue);
      }
    });
    syncModalToColor(colorValue);
  }

  /* Full sync: swap images to the current color, then badges (valid variant). */
  function syncSelection() {
    applyColorEverywhere(currentColor());

    var script = document.querySelector('[data-selected-variant]');
    if (!script) return;
    var variant = null;
    try {
      variant = JSON.parse(script.textContent);
    } catch (e) {}
    if (variant && variant.id) {
      syncBadgesToVariant(variant);
      syncSpecToVariant(variant);
      syncVltToVariant(variant);
    }
  }

  /* Run once on load (handles URL-deep-linked variant/color) */
  syncSelection();

  /* React the instant a color is clicked — before Dawn's AJAX — so the image
     swaps even if the resulting Color × Strap combo is unavailable. Also keep
     the timeout/observer re-syncs for badges + Dawn's gallery re-render. */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('.product-form__input input[type="radio"]');
    if (!input) return;
    if (Object.prototype.hasOwnProperty.call(colorMediaMap, input.value)) {
      /* Keep the variant available by snapping the strap to a compatible value,
         then swap the gallery image to the chosen color immediately. */
      ensureValidStrapForColor(input.value);
      applyColorEverywhere(input.value);
    }
    setTimeout(syncSelection, 50);
    setTimeout(syncSelection, 250);
    setTimeout(syncSelection, 600);
  });

  /* Watch the selected-variant JSON for in-place text changes. */
  var selectedVariantScript = document.querySelector('[data-selected-variant]');
  if (selectedVariantScript) {
    new MutationObserver(syncSelection).observe(selectedVariantScript, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  /* Most reliable signal for the valid-variant case: Dawn swaps the gallery
     <li>s via async view transitions. Re-sync when the gallery children change. */
  document.querySelectorAll('.product__media-list').forEach(function (track) {
    new MutationObserver(syncSelection).observe(track, { childList: true });
  });
});
